'use client';

import Skeleton from '@carvana/ds-skeleton';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { isProd } from '~/constants/environment';
import {
  oneTrustPrivacyNoticeId,
  oneTrustPrivacyNoticeSettings,
  oneTrustPrivacyNoticeUrl
} from '~/constants/oneTrust/oneTrustPrivacyNotice';
import { logError } from '~/utilities/loggers';

const LOAD_TIMEOUT_MS = 8000;

const TOC_ROW_WIDTHS = [
  ['110px', '220px', '260px'],
  ['260px', '280px'],
  ['260px', '110px', '110px', '110px'],
  ['360px']
];

type SkeletonBlock = { lines: Array<string>; indent?: boolean };
type SkeletonSection = { headingWidth: string; blocks: Array<SkeletonBlock> };

const SECTIONS: Array<SkeletonSection> = [
  {
    headingWidth: '110px',
    blocks: [
      { lines: ['100%', '100%', '100%', '100%', '100%', '100%', '80%'] },
      { lines: ['100%', '100%', '100%', '100%', '60%'] },
      { lines: ['100%', '100%', '90%'] },
      { lines: ['100%', '80%'] },
      { lines: ['100%', '100%', '100%', '90%'] }
    ]
  },
  {
    headingWidth: '300px',
    blocks: [
      { lines: ['70%'] },
      { lines: ['95%'], indent: true },
      { lines: ['90%'], indent: true },
      { lines: ['85%'], indent: true },
      { lines: ['95%'], indent: true },
      { lines: ['100%', '80%'] }
    ]
  },
  {
    headingWidth: '340px',
    blocks: [
      { lines: ['65%'] },
      { lines: ['90%'], indent: true },
      { lines: ['85%'], indent: true },
      { lines: ['95%'], indent: true },
      { lines: ['80%'], indent: true },
      { lines: ['100%', '90%'] }
    ]
  }
];

declare global {
  interface Window {
    OneTrust?: {
      NoticeApi?: {
        Initialized: Promise<void>;
        LoadNotices: (urls: Array<string>) => Promise<void>;
      };
      ToggleInfoDisplay?: () => void;
    };
  }
}

export default function OneTrustPrivacyNotice() {
  const [hasError, setHasError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const mode = isProd ? 'published' : 'draft';

  useEffect(() => {
    if (hasLoaded || hasError) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      logError(new Error('OneTrust privacy notice load timed out'), {
        location: 'OneTrustPrivacyNotice',
        mode
      });
      setHasError(true);
    }, LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [hasLoaded, hasError, mode]);

  if (hasError) {
    return (
      <p className="t-body-md text-text-secondary">
        We’re unable to load this page’s content right now. Please try again
        later.
      </p>
    );
  }

  return (
    <>
      {!hasLoaded && (
        <div
          className="flex min-h-screen flex-col gap-y-2xl"
          aria-hidden="true"
          data-testid="privacy-notice-skeleton"
        >
          <div className="flex flex-col items-center gap-y-sm rounded-md border border-border-weak bg-background-default p-xl">
            {TOC_ROW_WIDTHS.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="flex flex-wrap justify-center gap-x-lg gap-y-sm"
              >
                {row.map((width, columnIndex) => (
                  <Skeleton key={columnIndex} height={16} width={width} />
                ))}
              </div>
            ))}
          </div>
          {SECTIONS.map((section, sectionIndex) => (
            <div key={sectionIndex} className="flex flex-col gap-y-lg">
              <Skeleton
                height={20}
                width={section.headingWidth}
                className="mx-auto"
              />
              {section.blocks.map((block, blockIndex) => (
                <div
                  key={blockIndex}
                  className={
                    block.indent
                      ? 'flex flex-col gap-y-sm pl-lg'
                      : 'flex flex-col gap-y-sm'
                  }
                >
                  {block.lines.map((width, lineIndex) => (
                    <Skeleton key={lineIndex} height={16} width={width} />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div
        id={`otnotice-${oneTrustPrivacyNoticeId}`}
        className={hasLoaded ? 'otnotice' : 'otnotice hidden'}
      />
      <Script
        id="otprivacy-notice-script"
        src="https://privacyportal-cdn.onetrust.com/privacy-notice-scripts/otnotice-1.0.min.js"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `settings="${oneTrustPrivacyNoticeSettings[mode]}"`
        }}
        onError={error => {
          logError(error, { location: 'OneTrustPrivacyNotice', mode });
          setHasError(true);
        }}
        onLoad={() => {
          window.OneTrust?.NoticeApi?.Initialized.then(
            () =>
              window.OneTrust?.NoticeApi?.LoadNotices([
                oneTrustPrivacyNoticeUrl[mode]
              ])
          )
            .then(() => {
              setHasLoaded(true);
            })
            .catch(error => {
              logError(error, { location: 'OneTrustPrivacyNotice', mode });
              setHasError(true);
            });
        }}
      />
    </>
  );
}
